import { useReducedMotion } from "@reactuses/core";
import { useMessageScroller } from "@shadcn/react/message-scroller";
import {
    createContext,
    type ReactNode,
    use,
    useEffect,
    useEffectEvent,
    useState,
} from "react";
import {
    getChatScrollerViewport,
    getMessageScrollerItem,
    getTopmostVisibleMessageId,
    isMessageFullyInScrollerViewport,
    waitForMessageElement,
    waitForScrollerSettle,
} from "../-utils/scrollToReplyTarget";
import { useChatPage } from "./useChatPage";

type ReturnTarget = {
    messageId: string;
    behavior: ScrollBehavior;
};

type MessageJumpContextValue = {
    pendingMessageId: string | null;
    returnMessageId: string | null;
    jumpToMessage: (messageId: string) => void;
    returnFromJump: () => void;
};

const MessageJumpContext = createContext<MessageJumpContextValue | null>(null);

export function MessageJumpProvider({ children }: { children: ReactNode }) {
    const { fetchPreviousUntilMessageLoaded, threadActions } = useChatPage();
    const { scrollToMessage } = useMessageScroller();
    const isReducedMotion = useReducedMotion();
    const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
    const [returnTarget, setReturnTarget] = useState<ReturnTarget | null>(null);

    const jumpToMessage = useEffectEvent(async (messageId: string) => {
        if (pendingMessageId) {
            return;
        }

        if (isMessageFullyInScrollerViewport(messageId)) {
            threadActions.onHighlightMessage(messageId);
            return;
        }

        const viewport = getChatScrollerViewport();
        const returnTo = viewport ? getTopmostVisibleMessageId(viewport) : null;
        const wasAlreadyRendered = getMessageScrollerItem(messageId) !== null;

        if (!wasAlreadyRendered) {
            setPendingMessageId(messageId);
            const isLoaded = await fetchPreviousUntilMessageLoaded(messageId);
            const element = isLoaded ? await waitForMessageElement(messageId) : null;
            setPendingMessageId(null);

            if (!element) {
                return;
            }
        }

        // Animating across freshly prepended history would crawl, so only short
        // hops animate. The trip back covers the same distance, so it reuses this.
        const behavior: ScrollBehavior =
            wasAlreadyRendered && !isReducedMotion ? "smooth" : "auto";

        scrollToMessage(messageId, { align: "center", behavior });

        if (viewport) {
            await waitForScrollerSettle(viewport);
        }

        threadActions.onHighlightMessage(messageId);
        setReturnTarget(returnTo ? { messageId: returnTo, behavior } : null);
    });

    const returnFromJump = useEffectEvent(() => {
        if (!returnTarget) {
            return;
        }

        scrollToMessage(returnTarget.messageId, {
            align: "start",
            behavior: returnTarget.behavior,
        });
        setReturnTarget(null);
    });

    useEffect(
        function dismissReturnWhenOriginVisible() {
            const originId = returnTarget?.messageId;
            const viewport = getChatScrollerViewport();

            if (!originId || !viewport) {
                return;
            }

            let frame = 0;
            const onScroll = () => {
                if (frame !== 0) {
                    return;
                }

                frame = window.requestAnimationFrame(() => {
                    frame = 0;

                    if (isMessageFullyInScrollerViewport(originId)) {
                        setReturnTarget(null);
                    }
                });
            };

            viewport.addEventListener("scroll", onScroll, { passive: true });

            return () => {
                window.cancelAnimationFrame(frame);
                viewport.removeEventListener("scroll", onScroll);
            };
        },
        [returnTarget],
    );

    return (
        <MessageJumpContext
            value={{
                pendingMessageId,
                returnMessageId: returnTarget?.messageId ?? null,
                jumpToMessage,
                returnFromJump,
            }}
        >
            {children}
        </MessageJumpContext>
    );
}

export function useMessageJump(): MessageJumpContextValue {
    const value = use(MessageJumpContext);

    if (!value) {
        throw new Error("useMessageJump must be used within MessageJumpProvider");
    }

    return value;
}
